import React from 'react';

import { css } from '@emotion/css';
import { Bar, CartesianGrid, ComposedChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';
import { colors } from 'theme/colors';

import { FlexSpace } from 'components/FlexSpace';
import { Typography } from 'components/Typography';

export interface StackedBarData {
  label: string;
  [key: string]: string | number;
}

export interface BarSegment {
  key: string;
  color: string;
  title: string;
}

export interface StackedBarChartProps {
  data: StackedBarData[];
  bars: BarSegment[];
  valueFormatter: (value: number) => string;
  valueType?: 'currency' | 'number' | 'percent';
  height?: number;
  /** Set false when the value is a count, so small ranges don't get fractional axis ticks. */
  allowDecimals?: boolean;
}

export function StackedBarChart({
  data,
  bars,
  valueFormatter,
  valueType = 'number',
  height = 230,
  allowDecimals = true,
}: StackedBarChartProps) {
  return (
    <ResponsiveContainer width="99.9%" height={height}>
      <ComposedChart data={data}>
        <XAxis dataKey="label" tick={{ fill: colors.primary[600], stroke: colors.primary[600], strokeWidth: 0.2 }} />
        <YAxis
          type="number"
          domain={valueType === 'percent' ? [0, 1] : [0, 'auto']}
          allowDecimals={allowDecimals}
          tickFormatter={valueFormatter}
          tick={{ fill: colors.primary[600], stroke: colors.primary[600], strokeWidth: 0.2 }}
        />

        {/* Render all bars */}
        {bars.map((bar) => (
          <Bar key={bar.key} dataKey={bar.key} stackId="stack" fill={bar.color} isAnimationActive={false} />
        ))}

        <Tooltip
          content={({ active, payload }: TooltipProps) => {
            if (!active || !payload || payload.length === 0) return null;

            const dataPayload = payload[0].payload;

            // Filter bars that have non-zero values
            const visibleBars = bars.filter((bar) => {
              const value = dataPayload[bar.key];
              return value !== undefined && value !== 0;
            });

            if (visibleBars.length === 0) return null;

            return (
              <div
                className={css`
                  background-color: rgba(255, 255, 255, 0.95);
                  padding: 12px;
                  border-radius: 8px;
                  border: 2px solid ${colors.neutral[400]};
                `}
              >
                <Typography style={{ fontWeight: 600, marginBottom: 8 }}>{dataPayload.label}</Typography>
                {visibleBars.map((bar) => {
                  const value = dataPayload[bar.key];

                  return (
                    <FlexSpace key={bar.key} gap={8}>
                      <div
                        className={css`
                          width: 12px;
                          height: 12px;
                          background-color: ${bar.color};
                          border-radius: 2px;
                        `}
                      />
                      <Typography>
                        {bar.title}: {valueFormatter(value)}
                      </Typography>
                    </FlexSpace>
                  );
                })}
              </div>
            );
          }}
        />
        <CartesianGrid strokeDasharray="3 3" />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
